interface RGB {
  r: number;
  g: number;
  b: number;
}

export function getReadableOverlayColor(backgroundColor: string | undefined, fallbackBackgroundColor: string, opacity = 0.68) {
  const rgb = parseColor(backgroundColor || fallbackBackgroundColor) || parseColor(fallbackBackgroundColor);

  if (!rgb) {
    return `rgba(255,255,255,${opacity})`;
  }

  return getRelativeLuminance(rgb) > 0.45
    ? `rgba(0,0,0,${opacity})`
    : `rgba(255,255,255,${opacity})`;
}

function parseColor(color: string | undefined): RGB | undefined {
  if (!color) {
    return undefined;
  }

  const normalizedColor = color.trim().toLowerCase();

  if (normalizedColor.startsWith('#')) {
    return parseHexColor(normalizedColor);
  }

  const rgbMatch = normalizedColor.match(/^rgba?\(([^)]+)\)$/);
  if (rgbMatch) {
    const [r, g, b] = rgbMatch[1].split(',').map((part) => Number(part.trim()));
    const rgb = { r, g, b };
    if (isValidRGB(rgb)) {
      return rgb;
    }
  }

  return NAMED_COLORS[normalizedColor];
}

function parseHexColor(color: string): RGB | undefined {
  const hex = color.replace('#', '');

  if (hex.length === 3 || hex.length === 4) {
    const [r, g, b] = hex.split('').map((part) => parseInt(`${part}${part}`, 16));
    return isValidRGB({ r, g, b }) ? { r, g, b } : undefined;
  }

  if (hex.length === 6 || hex.length === 8) {
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    return isValidRGB({ r, g, b }) ? { r, g, b } : undefined;
  }

  return undefined;
}

function isValidRGB(color: RGB) {
  return [color.r, color.g, color.b].every((channel) => (
    Number.isFinite(channel) && channel >= 0 && channel <= 255
  ));
}

function getRelativeLuminance(color: RGB) {
  const [r, g, b] = [color.r, color.g, color.b].map((channel) => {
    const value = channel / 255;
    return value <= 0.03928
      ? value / 12.92
      : Math.pow((value + 0.055) / 1.055, 2.4);
  });

  return (0.2126 * r) + (0.7152 * g) + (0.0722 * b);
}

const NAMED_COLORS: Record<string, RGB> = {
  black: { r: 0, g: 0, b: 0 },
  blue: { r: 0, g: 0, b: 255 },
  gray: { r: 128, g: 128, b: 128 },
  green: { r: 0, g: 128, b: 0 },
  grey: { r: 128, g: 128, b: 128 },
  orange: { r: 255, g: 165, b: 0 },
  purple: { r: 128, g: 0, b: 128 },
  red: { r: 255, g: 0, b: 0 },
  white: { r: 255, g: 255, b: 255 },
  yellow: { r: 255, g: 255, b: 0 },
};
